'use client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { Lang } from './i18n';

interface LangContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  switchLang: (l: Lang) => void;
  isRTL: boolean;
}

const LangContext = createContext<LangContextValue>({
  lang: 'en',
  setLang: () => {},
  switchLang: () => {},
  isRTL: false,
});

export function LangProvider({ children }: { children: React.ReactNode }) {
  // Plain default — no localStorage access during initialization
  // Server renders 'en', client hydrates with 'en' → no mismatch
  const [lang, setLang] = useState<Lang>('en');

  useEffect(() => {
    // Sync from localStorage after hydration (runs only on client)
    const stored = localStorage.getItem('votly_lang') as Lang | null;
    if (stored === 'ar' || stored === 'en') {
      setLang(stored);
    }
  }, []);

  useEffect(() => {
    // Keep document attributes in sync whenever lang changes
    document.documentElement.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  useEffect(() => {
    // Listen for storage changes (cross-tab or cross-provider-instance sync)
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'votly_lang') {
        const newLang = e.newValue as Lang | null;
        if (newLang === 'ar' || newLang === 'en') {
          setLang(newLang);
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const switchLang = useCallback((l: Lang) => {
    setLang(l);
    localStorage.setItem('votly_lang', l);
  }, []);

  return (
    <LangContext.Provider value={{ lang, setLang: switchLang, switchLang, isRTL: lang === 'ar' }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  return useContext(LangContext);
}